import { create } from 'zustand';
import { Logger } from '../lib/app-log.js';

const useCompanyStore = create((set, get) => ({
  // State
  companyData: null,
  committeeMembers: [],
  loading: false,
  error: null,
  success: null,
  isFormOpen: false,
  isEditMode: false,
  formData: null,

  // Actions
  fetchCompanyInfo: async () => {
    set({ loading: true, error: null });
    try {
      const { companyIntegration } = await import('@/lib/api/client');
      const result = await companyIntegration.fetchAll({ page: 1, pageSize: 1 });
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch company information');
      }
      const payload = Array.isArray(result.data) ? result.data[0] : result.data;
      set({ 
        companyData: payload || null, 
        loading: false,
        success: null
      });
    } catch (error) {
      set({ 
        error: error.message, 
        loading: false 
      });
    }
  },

  fetchCommitteeMembers: async () => {
    try {
      const { userIntegration } = await import('@/lib/api/client');
      const { useUserStore } = await import('@/store/user.store');
      await userIntegration.fetchUsersForDropdown();
      const usersForDropdown = useUserStore.getState().usersForDropdown || [];
      set({
        committeeMembers: usersForDropdown.map((item) => ({
          id: item.value,
          name: item.label,
          title: "",
        })),
      });
    } catch (error) {
      Logger.error('Error fetching committee members:', { error: error?.message });
      set({ committeeMembers: [] });
    }
  },

  updateCompany: async (companyData) => {
    set({ loading: true, error: null });
    try {
      const { companyIntegration } = await import('@/lib/api/client');
      const current = get().companyData;
      const currentId = current?._id || current?.id;
      const result = currentId
        ? await companyIntegration.update(currentId, companyData)
        : await companyIntegration.create(companyData);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update company information');
      }
      set({ 
        companyData: result.data, 
        loading: false,
        success: null,
        isFormOpen: false,
        isEditMode: false,
        formData: null
      });
    } catch (error) {
      set({ 
        error: error.message, 
        loading: false 
      });
    }
  },

  // Form management
  openEditForm: () => {
    const { companyData } = get();
    set({
      isFormOpen: true,
      isEditMode: true,
      formData: companyData ? {
        name: companyData.name || '',
        address: {
          street: companyData.address?.street || '',
          city: companyData.address?.city || '',
          state: companyData.address?.state || '',
          country: companyData.address?.country || 'Türkiye',
          postalCode: companyData.address?.postalCode || '',
        },
        contact: {
          email: companyData.contact?.email || '',
          phone: companyData.contact?.phone || '',
          website: companyData.contact?.website || '',
        },
      } : null
    });
  },

  closeForm: () => {
    set({
      isFormOpen: false,
      isEditMode: false,
      formData: null
    });
  },

  updateFormField: (field, value) => {
    const { formData } = get();
    if (formData) {
      set({
        formData: {
          ...formData,
          [field]: value
        }
      });
    }
  },

  // Message management
  clearError: () => set({ error: null }),
  clearSuccess: () => set({ success: null }),

  // Reset store
  reset: () => set({
    companyData: null,
    committeeMembers: [],
    loading: false,
    error: null,
    success: null,
    isFormOpen: false,
    isEditMode: false,
    formData: null,
  })
}));

export { useCompanyStore };
